<template lang="pug">
.box.cell-menu-action
    .box-header.b-b
        h3 Update Cell
    .box-body
        template(v-if="cell.action_type == 'product'")
            div.form-group
                label Product
                multiselect(
                    v-model="cell.action",
                    :internal-search="false",
                    :options="productList",
                    :option-height="104",
                    :limit="10",
                    :loading="onLoading('fetch-products')", 
                    :show-labels="false",
                    placeholder="Type to search",
                    label="name",
                    track-by="id",
                    @search-change="getProducts"
                )
                    template(slot="option", slot-scope="props")
                        .row
                            .col-xs-2
                                img.img-responsive(:src="props.option.media_urls.length > 0 ? props.option.media_urls[0].s512 : '/img/slurp-500.png'", alt="Product image")
                            .p-l-0.col-xs-10
                                span.option__title {{ props.option.name }}
                                br
                                small.text-ellipsis {{ props.option.description }}


            div.form-group(v-if="cell.action.allow_media_override")
                label Grid image
                file-upload.w-full.m-b-0(
                    name="media"
                    accept="image/png,image/jpg"
                    extensions="jpg,png,jpeg"
                    ref="uploader"
                    v-model="media"
                    :drop="true"
                    :post-action="mediaApi"
                    @input-file="onInputFile"
                    style="display: none"
                )

                label.btn.btn-sm.btn-block.white.m-b-0(
                    for="media"
                    v-if="shouldShowAddMedia"
                )
                    span Add or drop image

                .media-preview(
                    :style="{width: previewWidth, height: previewHeight, 'background-image': previewUrl }"
                    v-if="previewUrl"
                )

                button.btn.btn-sm.btn-block.white.m-b-0(
                    v-if="!shouldShowAddMedia"
                    @click.stop="removeMedia"
                )
                    span Remove image

                .drop-active(
                    :class="{'drop-inside': dragging, 'drop-outside': !dragging}"
                    v-show="$refs.uploader && $refs.uploader.dropActive"
                    @dragleave="dragging = false"
                    @dragover="dragging = true"
                    @drop="dragging = false"
                )
                    .drop-body.p-a
                        .drop-note
                            | Drop image

        template(v-else-if="cell.action_type == 'variant'")
            div.form-group
                label Variant
                multiselect(
                    v-model="cell.action",
                    :internal-search="false",
                    :options="variantList",
                    :option-height="104",
                    :limit="10",
                    :loading="onLoading('fetch-variants')", 
                    placeholder="Type to search",
                    label="name",
                    track-by="id",
                    @search-change="getVariants",
                    :show-labels="false",
                    :custom-label="variantName"
                )
                    template(slot="option", slot-scope="props")
                        .row
                            .col-xs-2
                                img.img-responsive(
                                    :src="props.option.product.media_urls.length > 0 ? props.option.product.media_urls[0].s512 : '/img/slurp-500.png'",
                                    alt="Product image"
                                )
                            .p-l-0.col-xs-10
                                span.option__title {{ props.option.product.name }}
                                br
                                span._500.text-muted {{ props.option.name }}

        template(v-else-if="cell.action_type == 'promotion'")
            div.form-group
                label Promotion
                multiselect(
                    v-model="cell.action",
                    :internal-search="false",
                    :options="promotionList",
                    :option-height="104",
                    :limit="10",
                    :loading="onLoading('fetch-promotions')", 
                    placeholder="Type to search",
                    label="name",
                    track-by="id",
                    @search-change="getPromotions",
                    :show-labels="false"
                )
                    template(slot="option", slot-scope="props")
                        .row
                            .col-md-12
                                span.option__title {{ props.option.name }}
                                br
                                strong {{ promotionType(props.option) }}

        template(v-else-if="cell.action_type == 'group'")
            div.form-group
                label Display group
                multiselect(
                    v-model="cell.action",
                    :options="groupList",
                    placeholder="Select display group",
                    label="name",
                    track-by="id",
                )

        div.form-group.m-b-0
            color-picker(v-model="cell.color")
    
    .box-footer.b-t
        button.btn.btn-sm.btn-block.white(@click="updateCell", :disabled="cell.action === null") Apply
</template>

<script>
import { OrderLayoutEvent } from './order-layout-bus';
import { mapActions, mapGetters, mapMutations } from 'vuex';
import colorPicker from './order-layout-color-picker.vue';
import PromotionTypes from '@js/store/promotion-types';
import FileUpload from 'vue-upload-component';

export default {
    components: {
        colorPicker,
        FileUpload,
    },

    data() {
        return {
            cell: {
                color: '',
                action: null,
            },
            productList: [],
            variantList: [],
            promotionList: [],
            dragging: false,
            media: [],
            removeMediaId: null,
        };
    },

    computed: {
        ...mapGetters('orderLayout', [
            'orderLayout',
            'groupList',
            'activePage',
            'getPageCellByGrid',
            'getCellPendingMedia',
            'selectedGrid',
        ]),

        cellMediaId() {
            const cell = this.cell;

            return `cellmedia-${cell.x}-${cell.y}`;
        },

        hasOverrideMedia() {
            if (!this.cell.action.override_media_urls) {
                return false;
            }

            return !!this.cell.action.override_media_urls.length;
        },

        hasPendingMedia() {
            return !!this.media.length;
        },

        isRemovingOverrideMedia() {
            return this.removeMediaId != null;
        },

        mediaApi() {
            return Laroute.route('franchise.order_layout.page.media');
        },

        overrideMediaId() {
            if (this.hasOverrideMedia) {
                const omu = this.cell.action.override_media_urls;

                return omu[omu.length - 1].id;
            }

            return null;
        },

        previewHeight() {
            return '150px';
        },

        previewWidth() {
            const percentageWidth = 100 / this.orderLayout.width;
            return 150 + (150 * percentageWidth) / 100 + 'px';
        },

        previewUrl() {
            let url;

            if (this.hasOverrideMedia && !this.isRemovingOverrideMedia) {
                const omu = this.cell.action.override_media_urls;

                url = omu[omu.length - 1].original;
            }

            if (this.hasPendingMedia) {
                url = this.media[0].data.local_url;
            }

            if (url) {
                return `url(${url}`;
            }

            return null;
        },

        shouldShowAddMedia() {
            return (
                !this.hasPendingMedia &&
                this.hasOverrideMedia === this.isRemovingOverrideMedia
            );
        },
    },

    mounted() {
        OrderLayoutEvent.$on('edit-cell', grid => {
            const { page_id, x, y } = grid;
            const cell = this.getPageCellByGrid(page_id, x, y, true);
            this.setCell(cell);

            this.media = this.getCellPendingMedia(cell);
        });

        this.getProducts(null);
        this.getVariants(null);
        this.getPromotions(null);
    },

    methods: {
        ...mapActions('product', ['fetchProducts']),
        ...mapActions('variant', ['fetchVariants']),
        ...mapActions('promotion', ['fetchPromotions']),
        ...mapActions('orderLayout', [
            'setOrCreatePageCell',
            'setSelectedGrid',
        ]),

        ...mapMutations('orderLayout', [
            'addCellPendingMedia',
            'removeCellPendingMedia',
        ]),

        getProducts: _.debounce(async function(query) {
            this.startLoading('fetch-products');
            const { data } = await this.fetchProducts({
                query,
                limit: 15,
                outlet_id: this.orderLayout.outlet_id,
            });
            this.productList = data;
            this.endLoading('fetch-products');
        }, 500),

        getVariants: _.debounce(async function(query) {
            this.startLoading('fetch-variants');
            const { data } = await this.fetchVariants({
                query,
                limit: 15,
                outlet_id: this.orderLayout.outlet_id,
            });
            this.variantList = data;
            this.endLoading('fetch-variants');
        }, 500),

        getPromotions: _.debounce(async function(query) {
            this.startLoading('fetch-promotions');
            const { data } = await this.fetchPromotions({
                query,
                limit: 15,
                outlet_id: this.orderLayout.outlet_id,
            });
            this.promotionList = data;
            this.endLoading('fetch-promotions');
        }, 500),

        setCell(cell) {
            this.cell = Object.assign({}, cell);
            this.removeMediaId = this.cell.remove_media_id;
        },

        updateCell() {
            this.removeMediaId = null;
            this.cell.action_id = this.cell.action.id;
            this.cell.remove_media_id = this.overrideMediaId;
            this.setOrCreatePageCell(this.cell);
            this.setSelectedGrid();
            this.removeCellPendingMedia(this.cell);

            if (this.media.length) {
                const URL = window.URL || window.webkitURL;
                let local_url;

                if (this.media[0].data.local_url) {
                    local_url = this.media[0].data.local_url;
                } else if (URL && URL.createObjectURL) {
                    local_url = URL.createObjectURL(this.media[0].file);
                }

                this.media[0].data = {
                    order_layout_id: this.orderLayout.id,
                    cell_id: this.cell.id,
                    page_id: this.cell.page_id,
                    x: this.cell.x,
                    y: this.cell.y,
                    local_url,
                };

                this.addCellPendingMedia(this.media[0]);
            }
        },

        variantName(variant) {
            return `${variant.product.name} ${variant.name}`;
        },

        promotionType(promotion) {
            let type = PromotionTypes.find(t => t.value === promotion.type);
            return type ? type.name : 'Unknown';
        },

        onInputFile(newFile, oldFile, prevent) {
            if (newFile && (!oldFile || newFile.file !== oldFile.file)) {
                newFile.url = '';

                let URL = window.URL || window.webkitURL;

                if (URL && URL.createObjectURL) {
                    if (!newFile.data) {
                        newFile.data = {};
                    }

                    if (!newFile.data.local_url) {
                        newFile.data.local_url = URL.createObjectURL(
                            newFile.file,
                        );
                    }
                }
            }
        },

        removeMedia() {
            this.media = [];

            if (this.hasOverrideMedia) {
                this.removeMediaId = this.overrideMediaId;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
label {
    font-weight: 500;
}

.drop-active {
    height: 100%;
    left: 0;
    opacity: 0;
    position: absolute;
    top: 0;
    transition: opacity 200ms ease-in-out;
    width: 100%;
    z-index: 1;

    .drop-body {
        align-items: center;
        display: flex;
        height: 100%;
        justify-content: center;
        left: 0;
        position: absolute;
        top: 0;
        width: 100%;
    }

    .drop-note {
        align-items: center;
        border: 1px #ffffff dashed;
        color: #ffffff;
        display: flex;
        height: 100%;
        justify-content: center;
        width: 100%;
    }
}

.drop-outside {
    background-color: #ffffff;
    opacity: 0.8;
    transition: opacity 200ms ease-in-out;
}

.drop-inside {
    background-color: #81d4fa;
    color: rgba(0, 0, 0, 0.87);
    opacity: 1;
    transition: opacity 200ms ease-in-out;
}

.media-preview {
    background-repeat: no-repeat;
    background-size: contain;
}
</style>



// WEBPACK FOOTER //
// resources/assets/js/franchise_component/order-layout/order-layout-update-cell.vue